import { useEffect, useRef, useState } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import { Flex, theme } from 'antd';
import {
  DEVICE_HEIGHT,
  DEVICE_WIDTH,
  GUTTER,
  MOBILE_SURFACE,
  MOBILE_TEXT,
} from './constants';
import { DeviceScaleContext } from './deviceScaleContext';
import { useMobilePreview } from './mobilePreviewContext';
import { StatusBar } from './StatusBar';
import { useFitDeviceScale } from './useFitDeviceScale';

interface PhoneFrameProps {
  /** Sections, stacked as the app stacks them. Each owns its own gutters. */
  children: ReactNode;
}

/** Pointer travel that commits the gesture to an axis. */
const AXIS_THRESHOLD = 4;

/** A fingertip, so the replica reads as touched rather than clicked. Dark
 * outline under a white ring keeps it visible on either phone theme; the
 * pressed variant fills, the way a touch ripple does. */
const touchCursor = (fill: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28">' +
      '<circle cx="14" cy="14" r="11" fill="none" stroke="rgba(0,0,0,.45)" stroke-width="4"/>' +
      `<circle cx="14" cy="14" r="11" fill="${fill}" stroke="#fff" stroke-width="2"/>` +
      '</svg>'
  )}") 14 14, grab`;

const TOUCH_CURSOR = touchCursor('rgba(255,255,255,.15)');
const TOUCH_CURSOR_DOWN = touchCursor('rgba(255,255,255,.55)');

/** The element a horizontal drag should scroll: the nearest scrollable
 * ancestor of what was grabbed, searched no further than the feed itself. */
const horizontalScroller = (from: EventTarget | null, stopAt: HTMLElement) => {
  let node = from instanceof HTMLElement ? from : null;
  while (node && node !== stopAt) {
    // Overflowing alone is not enough — clipped-but-static content (a
    // truncated title) overflows too and must not answer a swipe.
    const overflow = getComputedStyle(node).overflowX;
    if (
      (overflow === 'auto' || overflow === 'scroll') &&
      node.scrollWidth - node.clientWidth > 1
    )
      return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Device chrome only — no knowledge of what it contains, so a home
 * page preview can stack any number of sections inside one frame.
 *
 * Laid out at the logical device size and CSS-scaled as a whole, so the
 * replica keeps its proportions in any host column instead of reflowing.
 */
export const PhoneFrame = ({ children }: PhoneFrameProps) => {
  const { token } = theme.useToken();
  const slotRef = useRef<HTMLDivElement>(null);
  const scale = useFitDeviceScale(slotRef);
  const { mobileTheme } = useMobilePreview();
  const surface = MOBILE_SURFACE[mobileTheme];
  const textColor = MOBILE_TEXT[mobileTheme];

  // Mouse drag scrolls like a phone swipe. Engages only after a few
  // pixels of movement, so clicks inside the frame stay clicks; touch
  // input scrolls natively and is left alone. The gesture commits to one
  // axis: vertical drives the feed, horizontal the shelf under the pointer.
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef({
    startX: 0,
    startY: 0,
    startScroll: 0,
    down: false,
    axis: null as null | 'x' | 'y',
    /** Horizontal gestures scroll this, not the feed. */
    scroller: null as HTMLElement | null,
    pointerId: -1,
  });
  // Cursor only — the drag itself never reads it, so a re-render is cheap.
  const [grabbing, setGrabbing] = useState(false);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startScroll: 0,
      down: true,
      axis: null,
      // Resolved on the press: once pointer capture is claimed, every later
      // event targets the feed and the grabbed shelf is unfindable.
      scroller: scrollRef.current
        ? horizontalScroller(e.target, scrollRef.current)
        : null,
      pointerId: e.pointerId,
    };
    setGrabbing(true);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    const feed = scrollRef.current;
    if (!state.down || !feed) return;
    // Pointer travel is screen pixels; scroll offsets are content pixels.
    // Undoing the scale is what keeps a swipe under the cursor.
    const dx = (e.clientX - state.startX) / scale;
    const dy = (e.clientY - state.startY) / scale;

    if (!state.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_THRESHOLD) return;
      state.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      // A horizontal grab with nothing scrollable beneath it does nothing at
      // all — falling back to the feed would scroll it sideways-by-accident.
      if (state.axis === 'x' && !state.scroller) return;
      state.startScroll =
        state.axis === 'x' ? (state.scroller?.scrollLeft ?? 0) : feed.scrollTop;
      feed.setPointerCapture(e.pointerId);
    }

    if (state.axis === 'x') {
      if (state.scroller) state.scroller.scrollLeft = state.startScroll - dx;
    } else {
      feed.scrollTop = state.startScroll - dy;
    }
    e.preventDefault();
  };

  const onPointerEnd = () => {
    const state = drag.current;
    if (!state.down) return;
    const scroll = scrollRef.current;
    // Capture outlives a release the page never saw; left held, the next
    // gesture starts retargeted to an area that thinks it is idle.
    if (scroll?.hasPointerCapture(state.pointerId)) {
      scroll.releasePointerCapture(state.pointerId);
    }
    state.down = false;
    state.axis = null;
    state.scroller = null;
    setGrabbing(false);
  };

  // A release outside the window — or in another window entirely — never
  // reaches the element, and the drag would stay armed until the pointer
  // came back and grabbed the feed uninvited.
  useEffect(() => {
    const onWindowMove = (e: globalThis.PointerEvent) => {
      if (e.buttons === 0) onPointerEnd();
    };
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerEnd, true);
    window.addEventListener('pointermove', onWindowMove, true);
    window.addEventListener('blur', onPointerEnd);
    return () => {
      window.removeEventListener('pointerup', onPointerEnd, true);
      window.removeEventListener('pointercancel', onPointerEnd, true);
      window.removeEventListener('pointermove', onWindowMove, true);
      window.removeEventListener('blur', onPointerEnd);
    };
  }, []);

  return (
    // The slot is measured and reserves the scaled footprint, so the host
    // page lays out around the phone's rendered size, not its logical one.
    <div
      ref={slotRef}
      style={{
        height: DEVICE_HEIGHT * scale,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: DEVICE_WIDTH,
          height: DEVICE_HEIGHT,
          flex: '0 0 auto',
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          display: 'flex',
          flexDirection: 'column',
          background: surface,
          color: textColor,
          borderRadius: 28,
          border: `1px solid ${token.colorBorderSecondary}`,
          paddingBottom: GUTTER,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: `10px ${GUTTER}px ${GUTTER}px`,
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          <StatusBar />
        </div>

        <div
          ref={scrollRef}
          className="phone-frame-scroll"
          // A drag must swipe, never select: a selection's autoscroll
          // fights the drag and throws the scroll to one end.
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            userSelect: 'none',
            cursor: grabbing ? TOUCH_CURSOR_DOWN : TOUCH_CURSOR,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onDragStart={(e) => e.preventDefault()}
        >
          <DeviceScaleContext.Provider value={scale}>
            {children}
          </DeviceScaleContext.Provider>
        </div>

        {/* Home indicator; currentColor so it follows the phone theme. */}
        <Flex justify="center" style={{ paddingTop: GUTTER, flexShrink: 0 }}>
          <div
            style={{
              width: 120,
              height: 5,
              borderRadius: 3,
              background: 'currentColor',
              opacity: 0.4,
            }}
          />
        </Flex>
      </div>
    </div>
  );
};
